import { IconButton, ImageListItem, ImageListItemBar } from "@mui/material";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { ImageItemProps } from "./Types";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import i18n from "../../i18n";
import { transformGreekToEnglish } from "../../lib/helperFuncs";
import { useTranslation } from "react-i18next";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { keyframes } from "@emotion/react";
import DialogAction from "../Dialog/DialogAction";

const blurAnimation = keyframes`
  0% {
    filter: blur(0px);
  }
  50% {
    filter: blur(8px);
  }
  100% {
    filter: blur(0px);
  }
`;

// eslint-disable-next-line max-params
function srcset(
  image: string,
  width: number,
  height: number,
  rows = 1,
  cols = 1
) {
  return {
    src: `${image}?w=${width * cols}&h=${height * rows}&fit=crop&auto=format`,
    srcSet: `${image}?w=${width * cols}&h=${height * rows
    }&fit=crop&auto=format&dpr=2 2x`,
  };
}

const ImageCard = (props: ImageItemProps) => {
  const { t } = useTranslation();

  const {
    onChangeRegistration,
    onSubmitFavorite,
    item,
    isLoading = false,
  } = props;
  const {
    is_published = false,
    favorite = false,
    img,
    subtitle = "",
    title = "",
    alwayShowBanner = false,
    displayFavorite = false,
    displayEditAndPublish = false,
    redirect,
    redirectOutsideApp = false,
    enableFavoriteButton = true,
  } = item;
  const [visibleTitle, setTitleVisible] = useState<number>();
  const [disableLink, setDisableLink] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(favorite);
  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    setIsFavorite(favorite);
  }, [favorite]);

  const cols = 1;
  const rows = 1;

  return (
    <>
      {/* redirectOutsideApp conditional checks needs refactoring */}
      {!redirectOutsideApp
        ? (
          <Link to={!disableLink
            ? `/${redirect}/${item.id}`
            : "#"}>
            <ImageListItem
              key={item.id}
              cols={cols}
              rows={rows}
              sx={{
                animation: isLoading
                  ? `${blurAnimation} 1.5s infinite`
                  : "",
              }}
            >
              <img
                {...srcset(img, 400, 250, rows, cols)}
                loading='lazy'
                onMouseOver={() => setTitleVisible(item.id)}
                onMouseOut={() => setTitleVisible(undefined)}
              />
              <ImageListItemBar
                sx={{
                  borderRadius: 20,
                  background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0) 100%)",
                }}
                position='top'
                actionIcon={
                  <>
                    {displayFavorite && (
                      <IconButton
                        disabled={!enableFavoriteButton}
                        onMouseOver={() => setDisableLink(true)}
                        onMouseOut={() => setDisableLink(false)}
                        sx={{ color: "white" }}
                        aria-label={`favorite ${title}`}
                        onClick={(e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsFavorite(!isFavorite);
                          onSubmitFavorite(item?.id, !isFavorite);
                        }}
                      >
                        {isFavorite === true
                          ? (
                            <FavoriteIcon
                              color='error'
                              sx={{
                                border: "solid 2px #fafafa",
                                borderRadius: "50%",
                                padding: 0.5,
                              }}
                            />
                          )
                          : (
                            <FavoriteBorderIcon
                              sx={{
                                border: "solid 2px #fafafa",
                                borderRadius: "50%",
                                padding: 0.5,
                              }}
                            />
                          )}
                      </IconButton>
                    )}
                    {displayEditAndPublish && (
                      <>
                        <IconButton
                          sx={{
                            borderRadius: "50%",
                            padding: 0.5,
                            color: "#ddd",
                            border: "solid",
                            mr: "3px",
                          }}
                          aria-label={`edit ${title}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenSubmit(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <DialogAction
                          open={openSubmit}
                          handleClose={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenSubmit(false);
                          }}
                          title={String(t("Επεξεργασία Καταχώρησης"))}
                          content={t(
                            "Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την επεξεργασία της καταχώρησης σας;"
                          )}
                          onAccept={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenSubmit(false);
                            navigate(
                              `/${item?.type === "location"
                                ? "location"
                                : "professionals"
                              }/edit/${item.id}?omeka=true`
                            );
                          }}
                        />
                        <IconButton
                          sx={{
                            borderRadius: "50%",
                            padding: 0.5,
                            color: "#ddd",
                            border: "solid",
                            mr: "3px",
                          }}
                          aria-label={`visible ${title}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenDialog(true);
                          }}
                        >
                          {is_published
                            ? (
                              <VisibilityIcon />
                            )
                            : (
                              <VisibilityOffIcon />
                            )}
                        </IconButton>
                        <DialogAction
                          open={openDialog}
                          handleClose={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenDialog(false);
                          }}
                          title={String(t("Αλλαγή Κατάστασης"))}
                          content={t(
                            "Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την αλλαγή κατάστασης της καταχώρησης σας;"
                          )}
                          onAccept={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenDialog(false);
                            onChangeRegistration(
                              item?.id,
                              !is_published,
                              item?.type
                            );
                          }}
                        />
                      </>
                    )}
                  </>
                }
              />
              {visibleTitle === item?.id || alwayShowBanner
                ? (
                  <ImageListItemBar
                    onMouseOver={() => setTitleVisible(item.id)}
                    onMouseOut={() => setTitleVisible(undefined)}
                    sx={{
                      borderRadiusBottom: 20,
                      background:
                    "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
                    }}
                    position='bottom'
                    title={
                      i18n.language === "en"
                        ? transformGreekToEnglish(`${title}`)
                        : title
                    }
                    subtitle={t(subtitle)}
                  />
                )
                : null}
            </ImageListItem>
          </Link>
        )
        : (
          <a href={redirect}>
            <ImageListItem key={item.id} cols={cols}
              rows={rows}>
              <img
                {...srcset(img, 400, 250, rows, cols)}
                loading='lazy'
                onMouseOver={() => setTitleVisible(item.id)}
                onMouseOut={() => setTitleVisible(undefined)}
              />
              <ImageListItemBar
                sx={{
                  borderRadius: 20,
                  background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0) 100%)",
                }}
                position='top'
                actionIcon={
                  <>
                    {displayFavorite && (
                      <IconButton
                        onMouseOver={() => setDisableLink(true)}
                        onMouseOut={() => setDisableLink(false)}
                        sx={{ color: "white" }}
                        aria-label={`favorite ${title}`}
                        onClick={() => {
                          setIsFavorite(!isFavorite);
                          onSubmitFavorite(item?.id, !isFavorite);
                        }}
                      >
                        {isFavorite === true
                          ? (
                            <FavoriteIcon
                              color='error'
                              sx={{
                                border: "solid 2px #fafafa",
                                borderRadius: "50%",
                                padding: 0.5,
                              }}
                            />
                          )
                          : (
                            <FavoriteBorderIcon
                              sx={{
                                border: "solid 2px #fafafa",
                                borderRadius: "50%",
                                padding: 0.5,
                              }}
                            />
                          )}
                      </IconButton>
                    )}
                    {displayEditAndPublish && (
                      <>
                        <IconButton
                          sx={{
                            borderRadius: "50%",
                            padding: 0.5,
                            color: "#ddd",
                            border: "solid",
                            mr: "3px",
                          }}
                          aria-label={`edit ${title}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/location/edit/${item.id}`);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          sx={{
                            borderRadius: "50%",
                            padding: 0.5,
                            color: "#ddd",
                            border: "solid",
                            mr: "3px",
                          }}
                          aria-label={`visible ${title}`}
                          onClick={(e) => {
                            e.preventDefault();
                            onChangeRegistration(
                              item?.id,
                              !is_published,
                              item?.type
                            );
                          }}
                        >
                          {is_published
                            ? (
                              <VisibilityIcon />
                            )
                            : (
                              <VisibilityOffIcon />
                            )}
                        </IconButton>
                      </>
                    )}
                  </>
                }
              />
              {visibleTitle === item?.id || alwayShowBanner
                ? (
                  <ImageListItemBar
                    onMouseOver={() => setTitleVisible(item.id)}
                    onMouseOut={() => setTitleVisible(undefined)}
                    sx={{
                      borderRadiusBottom: 20,
                      background:
                    "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
                    }}
                    position='bottom'
                    title={title}
                    subtitle={subtitle}
                  />
                )
                : null}
            </ImageListItem>
          </a>
        )}
    </>
  );
};

export default ImageCard;
